import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Clock, Loader2, ArrowRight, FileText } from 'lucide-react';
import { trackPurchaseEvent } from '../lib/tracking';
import PostPaymentIntakeForm from '../components/PostPaymentIntakeForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PagoExitosoPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const sessionId = searchParams.get('session_id');
  const isSaasSubscription = paymentInfo?.metadata?.product_kind === 'saas_subscription';
  const requiresIntake = paymentInfo?.metadata?.requires_intake !== 'false' && !isSaasSubscription;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`${API}/checkout/status/${sessionId}`);
        const data = await response.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentInfo(data);
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(checkPaymentStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId]);

  useEffect(() => {
    if (status !== 'success' || !paymentInfo || !sessionId) return;

    trackPurchaseEvent({
      sessionId,
      packageId: paymentInfo.metadata?.package_id || 'unknown-package',
      packageName: paymentInfo.metadata?.package_name || 'Servicio Neuralia369',
      value: paymentInfo.amount,
      currency: paymentInfo.currency?.toUpperCase() || 'USD'
    });
  }, [status, paymentInfo, sessionId]);

  const handleDownloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      const response = await fetch(`${API}/invoice/${sessionId}`);
      if (!response.ok) throw new Error('Error generating invoice');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura-neuralia369-${sessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Error al descargar la factura. Por favor, inténtalo de nuevo.');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-golden-2xl flex items-center justify-center">
      <div className="max-w-3xl mx-auto px-golden-md text-center">
        {status === 'checking' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-golden-2xl">
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-golden-lg animate-spin" />
            <h2 className="text-2xl mb-golden-md">Verificando pago...</h2>
            <p className="text-text-body">Por favor, espera mientras confirmamos tu pago.</p>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-golden-2xl">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
              className="w-24 h-24 mx-auto mb-golden-lg rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-primary" />
            </motion.div>
            <h2 className="text-2xl mb-golden-md">Pago <span className="gradient-text">exitoso</span></h2>
            <p className="text-text-body mb-golden-lg">
              {isSaasSubscription
                ? 'Tu suscripción se ha activado correctamente. Ya puedes usar tu nueva capa SaaS en cuanto completemos el acceso de producto y onboarding asociado al plan.'
                : 'Gracias por tu compra. Hemos recibido tu pago correctamente.'}
            </p>
            {paymentInfo && (
              <div className="bg-card p-golden-md rounded-xl mb-golden-lg text-left">
                <p className="text-text-muted text-sm mb-2">Resumen:</p>
                <p className="text-text-heading font-semibold">
                  {paymentInfo.metadata?.package_name || 'Servicio Neuralia369'}
                </p>
                <p className="text-primary text-lg font-bold">
                  ${paymentInfo.amount} {paymentInfo.currency?.toUpperCase()}
                </p>
                {isSaasSubscription && (
                  <p className="text-text-body text-sm mt-2" data-testid="subscription-summary-note">
                    Ciclo: {paymentInfo.metadata?.billing_cycle === 'annual' ? 'anual' : 'mensual'} · Estado: {paymentInfo.metadata?.subscription_status || 'activo'}
                  </p>
                )}
              </div>
            )}

            {/* Invoice Download Button */}
            <button
              onClick={handleDownloadInvoice}
              disabled={downloadingInvoice}
              className="btn-secondary w-full justify-center mb-golden-md"
              data-testid="download-invoice-btn"
            >
              {downloadingInvoice ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Generando factura...</>
              ) : (
                <><FileText className="w-5 h-5" /> Descargar Factura PDF</>
              )}
            </button>

            <p className="text-text-muted text-sm mb-golden-xl">
              {isSaasSubscription
                ? 'Te enviaremos los detalles de la suscripción y próximos pasos de activación. Este flujo no requiere briefing de servicio a medida.'
                : 'Te enviaremos un email con los detalles y próximos pasos. Completa el briefing y agenda la reunión inicial: el sistema asignará automáticamente un consultor humano disponible para arrancar con cercanía y contexto.'}
            </p>
            <Link to="/" className="btn-primary inline-flex" data-testid="back-home-btn">
              Volver al inicio
              <ArrowRight className="w-5 h-5" />
            </Link>

            {isSaasSubscription && (
              <Link to="/panel" className="btn-secondary inline-flex mt-golden-md" data-testid="go-workspace-btn">
                Ir a Neuralia Workspace
                <ArrowRight className="w-5 h-5" />
              </Link>
            )}

            {requiresIntake && <PostPaymentIntakeForm apiBase={API} sessionId={sessionId} paymentInfo={paymentInfo} />}
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-golden-2xl">
            <div className="w-24 h-24 mx-auto mb-golden-lg rounded-full bg-red-500/20 flex items-center justify-center">
              <svg className="w-12 h-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl mb-golden-md">Error en el pago</h2>
            <p className="text-text-body mb-golden-xl">
              Hubo un problema al procesar tu pago. Por favor, inténtalo de nuevo.
            </p>
            <Link to="/precios" className="btn-primary inline-flex">
              Intentar de nuevo
            </Link>
          </motion.div>
        )}

        {status === 'expired' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-golden-2xl">
            <div className="w-24 h-24 mx-auto mb-golden-lg rounded-full bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-12 h-12 text-yellow-500" />
            </div>
            <h2 className="text-2xl mb-golden-md">Sesión expirada</h2>
            <p className="text-text-body mb-golden-xl">
              La sesión de pago ha expirado. Por favor, inicia el proceso de nuevo.
            </p>
            <Link to="/precios" className="btn-primary inline-flex">
              Ir a precios
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PagoExitosoPage;
